import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FaRegCircleCheck } from "react-icons/fa6";
import debounce from "lodash.debounce";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextField from "@mui/material/TextField";
import moment from "moment";

const AvailabilitySchedule = ({ onSave }) => {
  const [activeTab, setActiveTab] = useState("monthly");
  const [selectedDates, setSelectedDates] = useState([1]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [duration, setDuration] = useState(60);

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Mapping from day names to numbers (0=Sunday, 1=Monday, etc.)
  const dayNameToNumber = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  // Helper function to format Date object to HH:MM:SS
  const formatTimeToHHMMSS = (date) => {
    if (!date || !(date instanceof Date)) return "00:00:00";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}:00`;
  };

  // Helper function to convert 12-hour time format to 24-hour format (HH:MM:SS)
  const convertTo24Hour = (timeStr) => {
    if (!timeStr) return "";

    // If already in 24-hour format, return as is
    if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeStr;
    }

    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return "00:00:00";

    let [, hours, minutes, period] = match;
    hours = parseInt(hours);

    if (period.toUpperCase() === "PM" && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes}:00`;
  };

  const formatTimeToTwelveHour = (time24) => {
    return moment(time24, "HH:mm:ss").format("hh:mm A");
  };

  // Helper function to generate ISO date strings for selected dates in current month
  const generateSpecificDates = (selectedDates) => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");

    return selectedDates
      .map((date) => {
        const day = date.toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      })
      .sort();
  };

  const handleDateClick = (date) => {
    if (activeTab === "monthly") {
      if (selectedDates.includes(date)) {
        setSelectedDates(selectedDates.filter((d) => d !== date));
      } else {
        setSelectedDates([...selectedDates, date]);
      }
    }
  };

  const handleDayClick = (day) => {
    if (activeTab === "weekly") {
      if (selectedDays.includes(day)) {
        setSelectedDays(selectedDays.filter((d) => d !== day));
      } else {
        setSelectedDays([...selectedDays, day]);
      }
    }
  };

  // Auto-calculate end time when start time or duration changes
  useEffect(() => {
    if (startTime && duration) {
      const calculatedEndTime = new Date(startTime.getTime() + duration * 60000);
      setEndTime(calculatedEndTime);
    }
  }, [startTime, duration]);

  const renderMonthlyContent = () => (
    <div>
      <Label>Select dates:</Label>
      <DateGrid>
        {[...Array(31)].map((_, i) => (
          <DateButton
            key={i + 1}
            type="button"
            selected={selectedDates.includes(i + 1)}
            onClick={() => handleDateClick(i + 1)}
          >
            {i + 1}
          </DateButton>
        ))}
      </DateGrid>
      <SelectedDates>Selected: {selectedDates.join(", ")}</SelectedDates>
    </div>
  );

  const renderWeeklyContent = () => (
    <div>
      <Label>Select days:</Label>
      <DayGrid>
        {daysOfWeek.map((day) => (
          <DayButton
            key={day}
            type="button"
            selected={selectedDays.includes(day)}
            onClick={() => handleDayClick(day)}
          >
            {day}
          </DayButton>
        ))}
      </DayGrid>
    </div>
  );

  const renderAlternateContent = () => (
    <div>
      <Label>Select start date:</Label>
      <StartDateInput
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
      />
    </div>
  );

  const renderDailyContent = () => (
    <div
      style={{
        textAlign: "center",
        padding: "40px 0",
        color: "#4A5565",
        fontFamily: "Montserrat",
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: "1.25rem",
        letterSpacing: "0px",
        textAlign: "center",
      }}
    >
      <FaRegCircleCheck
        color="#ED1E24"
        size={40}
        style={{ marginBottom: "1rem" }}
      />
      <p>You'll be available every day</p>
    </div>
  );

  const renderTimeSlots = () => (
    <div>
      <Label>Time Slot *</Label>
      <DurationContainer>
        <Label>Duration (minutes)</Label>
        <DurationSelect value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
          <option value={30}>30 minutes</option>
          <option value={45}>45 minutes</option>
          <option value={60}>60 minutes</option>
          <option value={90}>90 minutes</option>
        </DurationSelect>
      </DurationContainer>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimeSlotContainer>
          <TimePickerWrapper>
            <TimePicker
              label="Start time"
              value={startTime}
              onChange={(newValue) => setStartTime(newValue)}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </TimePickerWrapper>
          <Arrow>→</Arrow>
          <TimePickerWrapper>
            <TimePicker
              label="End time"
              value={endTime}
              onChange={(newValue) => setEndTime(newValue)}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </TimePickerWrapper>
        </TimeSlotContainer>
      </LocalizationProvider>
    </div>
  );

  const getSummary = () => {
    if (!startTime || !endTime) return "";
    
    const startStr = formatTimeToTwelveHour(formatTimeToHHMMSS(startTime));
    const endStr = formatTimeToTwelveHour(formatTimeToHHMMSS(endTime));
    
    if (
      activeTab === "monthly" &&
      selectedDates.length > 0
    ) {
      return `Month days ${selectedDates.join(", ")} selected from ${startStr} - ${endStr}`;
    } else if (
      activeTab === "weekly" &&
      selectedDays.length > 0
    ) {
      return `Days ${selectedDays.join(", ")} selected from ${startStr} - ${endStr}`;
    } else if (activeTab === "daily") {
      return `Available every day from ${startStr} - ${endStr}`;
    } else if (activeTab === "alternate" && startDate) {
      return `Starting from ${startDate} from ${startStr} - ${endStr}`;
    }
    return "";
  };

  // Build current schedule data
  const buildScheduleData = () => {
    // Convert Date objects to HH:MM:SS format
    const formattedTimeSlots = (startTime && endTime) ? [{
      start: formatTimeToHHMMSS(startTime),
      end: formatTimeToHHMMSS(endTime),
    }] : [];

    if (activeTab === "daily") {
      return {
        availability_type: "weekly",
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        time_slots: formattedTimeSlots,
        duration: duration,
      };
    }

    if (activeTab === "weekly") {
      // Convert day names to numbers
      const dayNumbers = selectedDays
        .map((dayName) => dayNameToNumber[dayName])
        .sort((a, b) => a - b);
      return {
        availability_type: "weekly",
        days_of_week: dayNumbers,
        time_slots: formattedTimeSlots,
        duration: duration,
      };
    }

    if (activeTab === "monthly") {
      return {
        availability_type: "specific_dates",
        specific_dates: generateSpecificDates(selectedDates),
        time_slots: formattedTimeSlots,
        duration: duration,
      };
    }

    // For alternate tab (keeping old format for now)
    return {
      scheduleType: activeTab,
      selectedDates: activeTab === "monthly" ? selectedDates : [],
      selectedDays: activeTab === "weekly" ? selectedDays : [],
      startDate: activeTab === "alternate" ? startDate : "",
      timeSlots: formattedTimeSlots,
    };
  };

  // Debounced save function
  const debouncedSave = useCallback(
    debounce((data) => {
      if (onSave) onSave(data);
    }, 1000),
    [onSave],
  );

  // Autosave on any relevant state change
  useEffect(() => {
    const data = buildScheduleData();
    console.log("Calling autosave")
    debouncedSave(data);


    // Cancel pending debounce on unmount
    return () => {
      debouncedSave.cancel();
    };
  }, [
    activeTab,
    selectedDates,
    selectedDays,
    startDate,
    startTime,
    endTime,
    duration,
  ]);

  return (
    <Container>
      <Header>
        <Title>Availability Schedule</Title>
        <Subtitle>
          Set your recurring availability pattern and time slots
        </Subtitle>
      </Header>

      <StyleLabel>Style 1: Tabbed Interface</StyleLabel>

      <Tabs>
        <Tab
          active={activeTab === "daily"}
          onClick={() => setActiveTab("daily")}
        >
          <TabIcon>📅</TabIcon> Daily
        </Tab>
        <Tab
          active={activeTab === "weekly"}
          onClick={() => setActiveTab("weekly")}
        >
          <TabIcon>📊</TabIcon> Weekly
        </Tab>
        {/* <Tab
          active={activeTab === "alternate"}
          onClick={() => setActiveTab("alternate")}
        >
          <TabIcon>↔️</TabIcon> Alternate
        </Tab> */}
        <Tab
          active={activeTab === "monthly"}
          onClick={() => setActiveTab("monthly")}
        >
          <TabIcon>📆</TabIcon> Monthly
        </Tab>
      </Tabs>

      <Content>
        {activeTab === "monthly" && renderMonthlyContent()}
        {activeTab === "weekly" && renderWeeklyContent()}
        {activeTab === "alternate" && renderAlternateContent()}
        {activeTab === "daily" && renderDailyContent()}

        {renderTimeSlots()}

        {getSummary() && (
          <Summary>
            <strong>Summary:</strong> {getSummary()}
          </Summary>
        )}
      </Content>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  /* max-width: 1000px; */
  width: 100%;
  margin: 20px auto;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;

  box-sizing: border-box;
  border: 1px solid rgba(229, 231, 235, 1);
  border-radius: 14px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1),
    0px 1px 2px -1px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 1);
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const StyleLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 15px;
`;

const Tabs = styled.div`
  display: flex;
  background-color: #f5f5f5;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const Tab = styled.div`
  flex: 1;
  padding: 12px 16px;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.active ? "#fff" : "transparent")};
  color: ${(props) => (props.active ? "#ff3b30" : "#333")};
  border: ${(props) => (props.active ? "1px solid #e0e0e0" : "none")};
  border-right: ${(props) => (props.active ? "none" : "1px solid #e0e0e0")};

  &:last-child {
    border-right: none;
  }

  &:hover {
    background-color: ${(props) => (props.active ? "#fff" : "#f0f0f0")};
  }
`;

const TabIcon = styled.span`
  margin-right: 8px;
`;

const Content = styled.div`
  padding: 20px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
  margin-bottom: 15px;
`;

const DateButton = styled.button`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: ${(props) => (props.selected ? "#ff3b30" : "#f5f5f5")};
  color: ${(props) => (props.selected ? "#fff" : "#333")};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? "#e6332a" : "#eee")};
  }
`;

const SelectedDates = styled.div`
  font-size: 12px;
  color: #ff3b30;
  margin-top: 5px;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 15px;
`;

const DayButton = styled.button`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: ${(props) => (props.selected ? "#ff3b30" : "#f5f5f5")};
  color: ${(props) => (props.selected ? "#fff" : "#333")};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? "#e6332a" : "#eee")};
  }
`;

const StartDateInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 10px;
`;

const TimeSlotContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const TimePickerWrapper = styled.div`
  flex: 1;

  .MuiTextField-root {
    width: 100%;
  }

  .MuiInputBase-root {
    border-radius: 10px;
  }
`;

const Arrow = styled.span`
  font-size: 18px;
  color: #666;
`;

const AddButton = styled.button.attrs({ type: "button" })`
  padding: 10px 15px;
  background-color: #ff3b30;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e6332a;
  }
`;

const DurationContainer = styled.div`
  margin-bottom: 15px;
`;

const DurationSelect = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff3b30;
  }
`;

const TimeSlotList = styled.div`
  min-height: 60px;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
  background-color: #fafafa;
`;

const TimeSlotItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #e8fdf5;
  border: 1px solid #d0f5e6;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 14px;
`;

const RemoveButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  color: #ff3b30;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;

  &:hover {
    color: #e6332a;
  }
`;

const NoTimeSlots = styled.div`
  text-align: center;
  color: #999;
  padding: 20px;
  font-size: 14px;
`;

const Summary = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #e8f4fe;
  border: 1px solid #cde6f7;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
`;

const CheckIcon = styled.div`
  font-size: 32px;
  color: #ff3b30;
  margin-bottom: 10px;
`;

export default AvailabilitySchedule;
